import Head from 'next/head'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { IOpenGraphData, ISeoData } from '../../model/Common/common.interface'
import { RINGLE_DOMAIN } from '../../modules/envVars'
import useAppRouter, { CustomRouter } from '../../modules/hooks/common/useAppRouter'
import { LOCALE_EN } from '../../modules/i18n/config'
import { s_common, s_head } from '../../modules/i18n/strings/auto/wording'
import useTranslation from '../../modules/i18n/useTranslation'
import { INVITE_RINGLE_MEMBER_PC_VER } from '../../modules/images'
import { CDN_URL, FACEBOOK_APP_ID } from '../../modules/vars'

// meta 태그는 Head 엘리먼트의 바로 자식 노드이거나. 1 레벨 깊이의 <Fragment>로 감싸져 잇어야 한다.
// 따라서 컴포넌트로 넣기보다 함수를 호출함

function getOriginPath(router: CustomRouter) {
  return '/' + router.asPath.split('?')[0].split('/').slice(2).join('/')
}

interface IBasicHead {
  seoData?: ISeoData
  ogData?: IOpenGraphData
  noIndex?: boolean
}

const BasicHead = ({ seoData, ogData, noIndex }: IBasicHead) => {
  const router = useAppRouter()
  const { t, isLocaleKO } = useTranslation()

  const [lang, setLang] = useState(router.query?.lang ?? LOCALE_EN)
  const [originPath, setOriginPath] = useState(getOriginPath(router))

  useEffect(() => {
    setOriginPath(getOriginPath(router))

    if (router.query.lang && !Array.isArray(router.query.lang)) {
      setLang(router.query.lang)
    }
  }, [router.pathname, router.query])

  const getValue = useCallback(
    (value: string, ogValue: string, fallbackValue: string) => (value ? value : ogValue ? ogValue : fallbackValue),
    []
  )

  const ogMain = useMemo(
    () =>
      router?.query.referralCode
        ? isLocaleKO
          ? INVITE_RINGLE_MEMBER_PC_VER('KR')
          : INVITE_RINGLE_MEMBER_PC_VER('EN')
        : `${CDN_URL}/web/og_thumbnail.png`,

    [router.query?.referralCode]
  )

  let doc = null

  doc = {
    name: t(s_common.ringle),
    title: getValue(seoData?.pageTitle, ogData?.title, isLocaleKO ? '링글 일대일 화상영어' : t(s_head.student.title)),
    desc: getValue(seoData?.pageDescription, ogData?.desc, t(s_head.student.desc)),
    keywords: getValue(seoData?.pageKeywords, ogData?.keywords, t(s_head.student.keywords)),
    author: ogData?.author ? ogData.author : t(s_head.student.author),
    og: {
      url: ogData?.ogUrl ? ogData.ogUrl : 'https://www.ringleplus.com/' + lang + originPath,
      type: 'website',
      siteName: ogData?.siteName ? ogData.siteName : t(s_common.ringle),
      title: ogData?.title ? ogData.title : t(s_head.student.title),
      description: ogData?.desc ? ogData.desc : t(s_head.student.desc),
      image: ogData?.image ? ogData.image : ogMain,
      imageSecureUrl: ogData?.image ? ogData.image : `${CDN_URL}/web/og_thumbnail.png`,
    },
    noscript: t(s_head.student.noscript),
  }

  const vars = {
    googleVerification: 'qqGVDMJYwbSlrfP8WpCyTJU4BgbsGS21M06nTejKjlA',
    naverVerification: 'eecb2aaf488f45f7202f76e6a3c5fd8ec36009e3',
  }

  return (
    <Head>
      <title>{doc.title}</title>
      {noIndex && <meta name="robots" content="noindex" />}
      <meta charSet="utf-8" />
      <meta name="naver-site-verification" content={vars.naverVerification} />
      <meta name="google-site-verification" content={vars.googleVerification} />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, shrink-to-fit=no, maximum-scale=1.0, user-scalable=0"
      />

      <meta name="name" content={doc.name} />
      <meta name="description" content={doc.desc} />
      <meta name="keywords" content={doc.keywords} />
      <meta property="og:url" content={`${doc.og.url}/`} />
      <meta property="og:type" content={doc.og.type} />
      <meta property="og:site_name" content={doc.og.siteName} />
      <meta property="og:title" content={doc.og.title} />
      <meta property="og:description" content={doc.og.description} />
      <meta property="og:image" content={doc.og.image} />
      <meta property="og:image:secure_url" content={doc.og.imageSecureUrl} />
      <meta property="fb:app_id" content={FACEBOOK_APP_ID} />

      {seoData?.pageCanonicalPath ? (
        <link rel="canonical" href={seoData?.pageCanonicalPath} />
      ) : (
        <link rel="canonical" href={'https://' + RINGLE_DOMAIN + '/' + lang + originPath} />
      )}

      <link rel="alternate" href={'https://' + RINGLE_DOMAIN + '/ko' + originPath} hrefLang="ko" />
      <link rel="alternate" href={'https://' + RINGLE_DOMAIN + '/en' + originPath} hrefLang="en" />
      <link rel="alternate" href={'https://' + RINGLE_DOMAIN + '/ja' + originPath} hrefLang="ja" />
      <link rel="alternate" href={'https://' + RINGLE_DOMAIN + '/vi' + originPath} hrefLang="vi" />
      <link rel="alternate" href={'https://' + RINGLE_DOMAIN + '/zh_cht' + originPath} hrefLang="zh-TW" />
      <link rel="alternate" href={'https://' + RINGLE_DOMAIN + '/zh_cht' + originPath} hrefLang="zh-HK" />
      <link rel="alternate" href={'https://' + RINGLE_DOMAIN + '/zh_chs' + originPath} hrefLang="zh-CN" />

      {/** noscript **/}
      <noscript>{doc.noscript}</noscript>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Organization',
            name: doc.name,
            description: doc.desc,
            url: 'https://' + RINGLE_DOMAIN + '/' + lang + originPath,
            sameAs: [
              'https://www.youtube.com/channel/UCG2DUiUkDuQckaRjkxqFUog/ringleplus.com',
              'https://www.facebook.com/ringleedu',
              'https://www.instagram.com/ringleedu',
              'https://blog.naver.com/ringleedu',
              'https://pf.kakao.com/_sYFgV',
              'https://play.google.com/store/apps/details?id=com.ringle',
              'https://apps.apple.com/app/ringle-1-1-online-english/id1189876556',
            ],
          }),
        }}
      />
    </Head>
  )
}

export default React.memo(BasicHead)
